
import React, { FunctionComponent, useMemo, useCallback } from 'react';
import { ACard, AChartX } from '@atiautomacao/ati-ui-library';
import { Box, Skeleton, useMediaQuery, useTheme } from '@mui/material';
import { useDailyReportPerformanceChartQuery } from '../reactQuery/DailyReportPerformanceChartReactQuery';
import {
    processChartData,
    hasValidChartData,
    calculateAxisInterval,
} from '../utils/DailyReportPerformanceChart.utils';
import DataNotFound from '../../../../Shared/Components/DataNotFoundMessage';
import type { AChartXOption, AChartXProps } from '@atiautomacao/ati-ui-library';
import { useAppSelector } from '../../../../Config/Hooks';

interface DailyReportPerformanceChartProps {
    selectedDate: string | null;
    powerStationIds?: number[] | null;
    height?: number | string;
    width?: number | string;
}

const CHART_CONFIG = {
    DEFAULT_HEIGHT: '328px',
    ANIMATION_DURATION: 1500,
    TOOLTIP_TRIGGER: 'axis' as const,
} as const;

const DailyReportPerformanceChart: FunctionComponent<DailyReportPerformanceChartProps> = ({
    selectedDate,
    powerStationIds,
    height = CHART_CONFIG.DEFAULT_HEIGHT,
    width = '100%',
}) => {

    const theme = useTheme();
    const smallScreen = useMediaQuery(theme.breakpoints.only("sm"));
    const largeOrExtraLargeScreen = useMediaQuery(theme.breakpoints.up("lg"));
    
    const openSubMenu = useAppSelector((state: any) => state.layout.openSubMenu);
    const { data: rawData, isSuccess, isLoading, isError } = useDailyReportPerformanceChartQuery(selectedDate, powerStationIds);


    const chartData = useMemo(() => {
        if (!isSuccess || !hasValidChartData(rawData)) {
            return null;
        }
        return processChartData(rawData);
    }, [rawData, isSuccess]);

    const leftAxisInterval = useMemo(() => {
        if (!chartData) return 0;
        return calculateAxisInterval(chartData.maxLeftAxisValue);
    }, [chartData]);

    const rightAxisInterval = useMemo(() => {
        if (!chartData) return 0;
        return calculateAxisInterval(chartData.maxRightAxisValue);
    }, [chartData]);

    // @ts-ignore
    const chartOption: AChartXOption = useMemo(() => {
        if (!chartData) {
            return {
                xAxis: { type: 'category', data: [] },
                yAxis: [],
                series: [],
            };
        }

        return {
            tooltip: {
                trigger: CHART_CONFIG.TOOLTIP_TRIGGER,
                axisPointer: {
                    type: 'cross',
                    label: {
                        backgroundColor: theme.palette.mode === 'dark' ? '#424242' : '#6a7985',
                    },
                },
                formatter: (params: any) => {
                    if (!Array.isArray(params)) return '';

                    const powerStationName = params[0]?.name || 'Usina';
                    let html = `<div style="font-weight: bold; margin-bottom: 8px;">${powerStationName}</div>`;

                    params.forEach((param: any) => {
                        const { seriesName, value, color } = param;
                        html += `<div style="color: ${color}; margin: 4px 0;">
                ${seriesName}: <strong>${value}</strong>
                </div>`;
                    });

                    return html;
                },
            },

            toolbox: {
                feature: {
                    dataView: { show: false, readOnly: false },
                    magicType: { show: false},
                    restore: { show: true },
                    saveAsImage: {
                        show: false,
                    },
                }
            },
            dataZoom: [{
                type: 'inside',
                zoomOnMouseWheel: false,
                moveOnMouseWheel: true,
                moveOnMouseMove: true,
                start: 0,
                end: chartData.xAxisData.length < 15 ? undefined : smallScreen ? 20 : 40,
            }],
            xAxis: {
                type: 'category',
                data: chartData.xAxisData,
                nameLocation: 'middle',
                nameGap: 20,
                axisTick: {
                    alignWithLabel: true,
                    show: true
                },
                axisLabel: {
                    interval: 0,
                    align: 'right',
                    margin: 12,
                    verticalAlign: 'middle',
                    rotate: 90,
                    fontSize: 10,
                    color: theme.palette.text.secondary,
                    overflow: largeOrExtraLargeScreen ? 'break' : 'truncate',
                    width: 48,
                    ellipsis: '...',
                },
            },
            
            yAxis: [
                {
                    type: 'value',
                    name: 'MWh',
                    min: 0,
                    max: chartData.maxLeftAxisValue,
                    interval: leftAxisInterval,
                    nameGap: 60,
                    nameLocation: 'middle',
                    position: 'left',
                    nameTextStyle: {
                        fontSize: 12,
                    },
                },
                {
                    type: 'value',
                    name: '%',
                    max: chartData.maxRightAxisValue,
                    min: 0,
                    interval: rightAxisInterval,
                    nameGap: 60,
                    position: 'right',
                    nameLocation: 'middle',
                    nameTextStyle: {
                        fontSize: 12,
                    },
                },
            ],
            legend: {
                show: true,
                type: 'scroll',
                orient: 'horizontal',
                bottom: -6,
                backgroundColor:
                    theme.palette.mode === 'dark'
                        ? 'rgba(66, 66, 66, 0.7)'
                        : 'rgba(255, 255, 255, 0.7)',
                textStyle: {
                    opacity: 1,
                    fontSize: 12,
                },
                itemStyle: {
                    opacity: 1,
                },
                data: chartData.seriesData.map(series => series.name),
            },
            grid: {
               containLabel: true,
                top: 10,
                bottom: 35,
                right: 55,
                left: 65
            },
            animationDuration: CHART_CONFIG.ANIMATION_DURATION,
            series: chartData.seriesData,
        };
    }, [chartData, theme, leftAxisInterval, rightAxisInterval, smallScreen, largeOrExtraLargeScreen]);

    const renderChartContent = useCallback(() => {
        if (isLoading) {
            return <Skeleton animation="wave" height={height} variant="rounded" width="100%" />;
        }

        if (isError || !isSuccess) {
            return <DataNotFound boxStyle={{ height, width: '100%' }} />;
        }

        if (!hasValidChartData(rawData)) {
            return <DataNotFound boxStyle={{ height, width: '100%' }} />;
        }

        return (
            <AChartX
                key={`performance-power-daily-${openSubMenu}`}
                option={chartOption as AChartXProps['option']}
                width={width}
                height={height}
                loading={isLoading}
                theme={theme.palette.mode}
            />
        );
    }, [isLoading, isError, isSuccess, rawData, height, width, chartOption, theme.palette.mode, openSubMenu]);

    return (
        <ACard
            ariaLabel="daily-report-performance-chart"
            styleProps={{
                cardStyle: { height: 'auto' },
                contentStyle: { padding: 16 },
            }}
            title="Desempenho das Usinas"
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                }}
            >
                {renderChartContent()}
            </Box>
        </ACard>
    );
};

export default DailyReportPerformanceChart;
